/**
 * Which tenant a new key is about to be issued for, in words a person can
 * check before pressing the button.
 *
 * The key goes to whatever the side-menu tenant selector points at, and the
 * modal used to say nothing about it: with two partners that share a name, or
 * with "All customers" still selected, the key could land somewhere the user
 * did not intend and they only found out from the list afterwards.
 */

export interface KeyTargetSelection {
  partnerId: number;
  partnerName: string;
  tenantId: number;
  tenantName: string;
  customerId?: string | null;
}

export interface KeyTargetUser {
  tenantId: number;
  tenantName?: string;
  tenantType: string;
}

export type KeyTarget =
  /** A tenant that can hold a key, and how to recognise it. */
  | { kind: "tenant"; name: string; detail: string }
  /**
   * The platform tenant itself. The backend refuses it — CTX's own data has
   * no place on a customer-facing API — so the form stops before asking.
   */
  | { kind: "platform" };

export function describeKeyTarget(
  selection: KeyTargetSelection,
  user: KeyTargetUser
): KeyTarget | null {
  // No selection means no `?tenantId`, and the backend then uses the
  // caller's own tenant.
  const id = selection.tenantId || user.tenantId;
  if (!id) return null;

  const isOwnTenant = id === user.tenantId;
  if (isOwnTenant && user.tenantType === "GLOBAL_ADMIN") return { kind: "platform" };

  if (isOwnTenant) {
    const name = selection.tenantName || user.tenantName || "";
    if (!name) return null;
    return {
      kind: "tenant",
      name,
      detail: user.tenantType === "PARTNER" ? "Partner account" : "Customer"
    };
  }

  // A GLOBAL_ADMIN looking at a partner's "All customers": the lens is the
  // partner itself, so that is who the key belongs to.
  if (selection.partnerId && id === selection.partnerId) {
    return {
      kind: "tenant",
      name: selection.partnerName || selection.tenantName,
      detail: "Partner account"
    };
  }

  const parent = selection.partnerName ? `Customer of ${selection.partnerName}` : "Customer";
  return {
    kind: "tenant",
    name: selection.tenantName,
    // The customer id is what tells apart two tenants with the same name;
    // it is the same one the side menu already shows.
    detail: selection.customerId ? `${parent} · ${selection.customerId}` : parent
  };
}
